// Array values (a key repeated in the query) stay repeated instead of being
// joined with commas, and a path that already has a query is extended with `&`.
export const frontendURL = (path, params) => {
  const entries = Object.entries(params || {}).flatMap(([key, value]) =>
    [value].flat().map(item => [key, item])
  );
  const query = new URLSearchParams(entries).toString();
  if (!query) return `/app/${path}`;
  return `/app/${path}${path.includes('?') ? '&' : '?'}${query}`;
};

export const conversationUrl = ({
  accountId,
  activeInbox,
  id,
  label,
  teamId,
  conversationType = '',
  foldersId,
  messageId,
}) => {
  let url = `accounts/${accountId}/conversations/${id}`;
  if (activeInbox) {
    url = `accounts/${accountId}/inbox/${activeInbox}/conversations/${id}`;
  } else if (label) {
    url = `accounts/${accountId}/label/${label}/conversations/${id}`;
  } else if (teamId) {
    url = `accounts/${accountId}/team/${teamId}/conversations/${id}`;
  } else if (foldersId && foldersId !== 0) {
    url = `accounts/${accountId}/custom_view/${foldersId}/conversations/${id}`;
  } else if (conversationType === 'mention') {
    url = `accounts/${accountId}/mentions/conversations/${id}`;
  } else if (conversationType === 'participating') {
    url = `accounts/${accountId}/participating/conversations/${id}`;
  } else if (conversationType === 'unattended') {
    url = `accounts/${accountId}/unattended/conversations/${id}`;
  }

  if (messageId) {
    url += `?messageId=${messageId}`;
  }

  return url;
};

// Query keys that belong to one conversation rather than to the list around it.
const CONVERSATION_ONLY_QUERY_KEYS = ['messageId'];

// The list reads its ad-hoc filters (display_ids from report links, phone_status,
// ratings, data assistant columns) from the URL, so moving between conversations
// has to carry them along or the list resets to the unfiltered inbox.
export const conversationListQuery = (query = {}) => {
  const listQuery = Object.fromEntries(
    Object.entries(query || {}).filter(
      ([key, value]) =>
        !CONVERSATION_ONLY_QUERY_KEYS.includes(key) &&
        value !== null &&
        value !== undefined &&
        value !== ''
    )
  );
  return Object.keys(listQuery).length ? listQuery : undefined;
};

export const conversationListPageURL = ({
  accountId,
  conversationType = '',
  inboxId,
  label,
  teamId,
  customViewId,
  query,
}) => {
  let url = `accounts/${accountId}/dashboard`;
  if (label) {
    url = `accounts/${accountId}/label/${label}`;
  } else if (teamId) {
    url = `accounts/${accountId}/team/${teamId}`;
  } else if (inboxId) {
    url = `accounts/${accountId}/inbox/${inboxId}`;
  } else if (customViewId) {
    url = `accounts/${accountId}/custom_view/${customViewId}`;
  } else if (conversationType) {
    const urlMap = {
      mention: 'mentions/conversations',
      unattended: 'unattended/conversations',
      participating: 'participating/conversations',
    };
    url = `accounts/${accountId}/${urlMap[conversationType]}`;
  }
  return frontendURL(url, conversationListQuery(query));
};

// The list a conversation was opened from, for routes that carry no list
// param (inbox, label, team and folder routes do).
const CONVERSATION_TYPE_BY_ROUTE_NAME = {
  conversation_mentions: 'mention',
  conversation_through_mentions: 'mention',
  conversation_unattended: 'unattended',
  conversation_through_unattended: 'unattended',
  conversation_participating: 'participating',
  conversation_through_participating: 'participating',
};

export const conversationListPageURLForRoute = ({ params, name, query }) => {
  const {
    accountId,
    inbox_id: inboxId,
    label,
    teamId,
    id: customViewId,
  } = params;
  return conversationListPageURL({
    accountId,
    inboxId,
    label,
    teamId,
    customViewId,
    conversationType: CONVERSATION_TYPE_BY_ROUTE_NAME[name] || '',
    query,
  });
};

export const isValidURL = value => {
  /* eslint-disable no-useless-escape */
  const URL_REGEX =
    /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/gm;
  return URL_REGEX.test(value);
};

export const getArticleSearchURL = ({
  host,
  portalSlug,
  pageNumber,
  locale,
  status,
  authorId,
  categorySlug,
  sort,
  query,
}) => {
  const queryParams = new URLSearchParams({});

  const params = {
    page: pageNumber,
    locale,
    status,
    author_id: authorId,
    category_slug: categorySlug,
    sort,
    query,
  };

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      queryParams.set(key, value);
    }
  });

  return `${host}/${portalSlug}/articles?${queryParams.toString()}`;
};

export const hasValidAvatarUrl = avatarUrl => {
  try {
    const { host: avatarUrlHost } = new URL(avatarUrl);
    const isFromGravatar = ['www.gravatar.com', 'gravatar'].includes(
      avatarUrlHost
    );
    return avatarUrl && !isFromGravatar;
  } catch (error) {
    return false;
  }
};

export const timeStampAppendedURL = dataUrl => {
  const url = new URL(dataUrl);
  if (!url.searchParams.has('t')) {
    url.searchParams.append('t', Date.now());
  }

  return url.toString();
};

export const getHostNameFromURL = url => {
  try {
    return new URL(url).hostname;
  } catch (error) {
    return null;
  }
};
